import { useAppSelector } from '@repo/store/maps/hooks';
import { selectPlayerLocationDeltas } from '@repo/store/maps/slices/mapData';
import type { GameSessionInternal, SessionTrailPoint } from '@repo/types/sessions/session-types';
import { formatVideoDurationSeconds } from '@repo/utility/time';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMapContext } from '../map-context';
import { isMapActive } from '../map-lifecycle';
import { EventMarkerRenderer } from '../renderers/event-marker-renderer';
import { type TrailRenderOptions, UnifiedTrailRenderer } from '../renderers/trail-renderer';
import { transformTrailCoordinates } from '../utils/coordinate-transform';
import { detectTeleports, euclidean } from '../utils/teleport-detection';

/**
 * Binary-search for the trail point index corresponding to a time-based progress fraction.
 */
function timeToTrailIndex(trail: SessionTrailPoint[], timeProgress: number): number {
  const trailStart = trail[0]?.timestamp ?? 0;
  const trailEnd = trail[trail.length - 1]?.timestamp ?? 0;
  const duration = trailEnd - trailStart;
  if (duration <= 0) return 0;
  const currentTime = trailStart + duration * timeProgress;
  let lo = 0;
  let hi = trail.length - 1;
  while (lo < hi) {
    const mid = lo + hi >>> 1;
    if ((trail[mid]?.timestamp ?? 0) < currentTime) lo = mid + 1;else hi = mid;
  }
  return lo;
}

/**
 * Convert time-based progress to distance-based progress for MapLibre's line-progress gradient.
 */
function timeToDistanceProgress(trail: SessionTrailPoint[], timeProgress: number, distances: {
  cumulative: number[];
  total: number;
} | null): number {
  if (trail.length <= 1 || timeProgress >= 1 || !distances) return timeProgress;
  const trailStart = trail[0]?.timestamp ?? 0;
  const trailEnd = trail[trail.length - 1]?.timestamp ?? 0;
  if (trailEnd - trailStart <= 0) return timeProgress;
  const idx = timeToTrailIndex(trail, timeProgress);
  return (distances.cumulative[idx] ?? 0) / distances.total;
}

/**
 * Convert time-based progress to index-based progress for segment visibility.
 * Returns the fraction of trail points traversed, which accounts for teleport
 * time gaps proportionally (unlike distance-based progress).
 */
function timeToIndexProgress(trail: SessionTrailPoint[], timeProgress: number): number {
  if (trail.length <= 1 || timeProgress >= 1) return timeProgress;
  if (timeProgress <= 0) return 0;
  const idx = timeToTrailIndex(trail, timeProgress);
  return idx / (trail.length - 1);
}
export interface SessionPlaybackState {
  progress: number;
  isPlaying: boolean;
  currentTime: number;
  totalDuration: number;
  playbackSpeed: number;
}
interface SessionTrailPlaybackOptions {
  visible?: boolean;
  targetRecapSeconds?: number;
  markers?: TrailRenderOptions['markers'];
  autoFitBounds?: boolean;
}

/**
 * Combined hook that manages both session playback controls AND trail rendering.
 * Handles play/pause/scrub controls while rendering animated trails to the map.
 */
export const useSessionTrailPlayback = (session: GameSessionInternal | null, options: SessionTrailPlaybackOptions = {}) => {
  const {
    visible = false,
    targetRecapSeconds = 20,
    markers,
    autoFitBounds = false
  } = options;
  const {
    map
  } = useMapContext();
  const playerLocationDeltas = useAppSelector(selectPlayerLocationDeltas);

  // Playback state
  const [state, setState] = useState<SessionPlaybackState>({
    progress: 1.0,
    isPlaying: false,
    currentTime: 0,
    totalDuration: 0,
    playbackSpeed: 5
  });
  const animationRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number>(0);
  const accumulatedTimeRef = useRef<number>(0);
  const isPlayingRef = useRef<boolean>(state.isPlaying);
  const playbackSpeedRef = useRef<number>(state.playbackSpeed);
  const hasFitBoundsRef = useRef(false);

  // Trail renderer
  const rendererRef = useRef<UnifiedTrailRenderer | null>(null);
  const eventRendererRef = useRef<EventMarkerRenderer | null>(null);
  const [isRendererReady, setIsRendererReady] = useState(false);
  const {
    trailCoordinates,
    filteredTrail
  } = useMemo(() => {
    if (!session?.trail || session.trail.length < 2) return {
      trailCoordinates: null,
      filteredTrail: [] as SessionTrailPoint[]
    };
    const raw = playerLocationDeltas ? transformTrailCoordinates(session, playerLocationDeltas) : session.trail.map(p => [p.x, p.y] as [number, number]);

    // Filter origin points from coordinates and trail in lockstep
    const keepIndices: number[] = [];
    for (let i = 0; i < raw.length; i++) {
      const coord = raw[i];
      if (coord && (coord[0] !== 0 || coord[1] !== 0)) {
        keepIndices.push(i);
      }
    }
    const coords = keepIndices.flatMap(i_0 => raw[i_0] ? [raw[i_0]] : []);
    const trail = keepIndices.flatMap(i_1 => session.trail[i_1] ? [session.trail[i_1]] : []);
    return {
      trailCoordinates: coords.length < 2 ? null : coords,
      filteredTrail: trail
    };
  }, [session, playerLocationDeltas]);

  // junkPointCount tracks how many leading trail points were filtered as
  // loading screen artifacts. Segment indices are remapped to be 0-based
  // relative to the filtered coordinate range.
  const {
    trailSegments,
    junkPointCount
  } = useMemo(() => {
    if (!trailCoordinates) return {
      trailSegments: null,
      junkPointCount: 0
    };
    const segments = detectTeleports(trailCoordinates);
    if (segments.length === 0) return {
      trailSegments: null,
      junkPointCount: 0
    };
    const offset = segments[0]?.startIndex ?? 0;
    const remapped = offset > 0 ? segments.map(seg => ({
      ...seg,
      startIndex: seg.startIndex - offset,
      endIndex: seg.endIndex - offset
    })) : segments;

    // Single unfiltered walk — use simpler single-LineString render path
    if (remapped.length === 1 && remapped[0]?.type === 'walk' && offset === 0) {
      return {
        trailSegments: null,
        junkPointCount: 0
      };
    }
    return {
      trailSegments: remapped,
      junkPointCount: offset
    };
  }, [trailCoordinates]);
  const effectiveTrail = useMemo(() => {
    if (filteredTrail.length === 0) return [];
    return junkPointCount > 0 ? filteredTrail.slice(junkPointCount) : filteredTrail;
  }, [filteredTrail, junkPointCount]);
  const effectiveCoordinates = useMemo(() => {
    if (!trailCoordinates) return undefined;
    return junkPointCount > 0 ? trailCoordinates.slice(junkPointCount) : trailCoordinates;
  }, [trailCoordinates, junkPointCount]);
  const totalDuration = useMemo(() => {
    if (effectiveTrail.length === 0) return 0;
    const trailDuration = (effectiveTrail[effectiveTrail.length - 1]?.timestamp ?? 0) - (effectiveTrail[0]?.timestamp ?? 0);
    return trailDuration > 0 ? trailDuration : session?.duration || 0;
  }, [effectiveTrail, session?.duration]);

  // MapLibre's line-progress is distance-based, so we precompute cumulative
  // distances to convert time-based scrubber progress into gradient fractions.
  const trailDistances = useMemo(() => {
    if (!effectiveCoordinates || effectiveCoordinates.length < 2) return null;
    const teleportGapIndices = new Set<number>();
    if (trailSegments) {
      for (const seg_0 of trailSegments) {
        if (seg_0.type === 'teleport') {
          teleportGapIndices.add(seg_0.startIndex);
        }
      }
    }
    let total = 0;
    const cumulative = [0];
    for (let i_2 = 1; i_2 < effectiveCoordinates.length; i_2++) {
      if (teleportGapIndices.has(i_2 - 1)) {
        cumulative.push(total);
      } else {
        const prev = effectiveCoordinates[i_2 - 1];
        const curr = effectiveCoordinates[i_2];
        if (prev && curr) {
          total += euclidean(prev, curr);
        }
        cumulative.push(total);
      }
    }
    if (total === 0) return null;
    return {
      cumulative,
      total
    };
  }, [effectiveCoordinates, trailSegments]);

  // Keep refs in sync with state
  useEffect(() => {
    isPlayingRef.current = state.isPlaying;
    playbackSpeedRef.current = state.playbackSpeed;
  }, [state.isPlaying, state.playbackSpeed]);

  // Initialize playback state when session changes
  useEffect(() => {
    if (session && totalDuration > 0) {
      hasFitBoundsRef.current = false;
      const autoSpeed = totalDuration / (targetRecapSeconds * 1000);
      setState(prev_0 => ({
        ...prev_0,
        totalDuration,
        progress: 1.0,
        currentTime: totalDuration,
        playbackSpeed: Math.max(0.1, autoSpeed || prev_0.playbackSpeed)
      }));
    }
  }, [targetRecapSeconds, session, session?.uuid, totalDuration]);

  // Animation loop
  const animate = useCallback((currentTime: number) => {
    if (!isPlayingRef.current || totalDuration <= 0) return;
    const deltaTime = currentTime - lastFrameTimeRef.current;
    lastFrameTimeRef.current = currentTime;
    if (deltaTime > 0 && deltaTime < 100) {
      const speedMultiplier = playbackSpeedRef.current;
      const timeIncrement = deltaTime * speedMultiplier;
      accumulatedTimeRef.current += timeIncrement;
      setState(prev_1 => {
        const newCurrentTime = Math.min(accumulatedTimeRef.current, totalDuration);
        const newProgress = newCurrentTime / totalDuration;
        if (newProgress >= 1.0) {
          return {
            ...prev_1,
            progress: 1.0,
            currentTime: totalDuration,
            isPlaying: false
          };
        }
        return {
          ...prev_1,
          progress: newProgress,
          currentTime: newCurrentTime
        };
      });
    }
    if (isPlayingRef.current) {
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [totalDuration]);

  // Start/stop animation loop
  useEffect(() => {
    if (state.isPlaying && totalDuration > 0) {
      lastFrameTimeRef.current = performance.now();
      accumulatedTimeRef.current = state.currentTime;
      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [state.isPlaying, animate, state.currentTime, totalDuration]);

  // Initialize trail renderer
  useEffect(() => {
    if (isMapActive(map) && !rendererRef.current) {
      rendererRef.current = new UnifiedTrailRenderer(map);
      eventRendererRef.current = new EventMarkerRenderer(map);
      setIsRendererReady(true);
    }
    return () => {
      rendererRef.current?.cleanup();
      rendererRef.current = null;
      eventRendererRef.current?.cleanup();
      eventRendererRef.current = null;
      setIsRendererReady(false);
    };
  }, [map]);

  // Render trail when session or progress changes, waiting for map to be ready
  useEffect(() => {
    if (!isRendererReady || !rendererRef.current || !isMapActive(map)) {
      return;
    }
    let cancelled = false;
    const renderTrail = () => {
      if (cancelled || !isMapActive(map)) return;
      if (rendererRef.current && visible && session) {
        const trailProgress = timeToDistanceProgress(effectiveTrail, state.progress, trailDistances);
        const indexProgress = timeToIndexProgress(effectiveTrail, state.progress);
        const shouldFitBounds = autoFitBounds && !hasFitBoundsRef.current;
        const hasCoords = effectiveCoordinates && effectiveCoordinates.length > 1;
        const fitBoundsNow = shouldFitBounds && hasCoords;
        rendererRef.current.render({
          session,
          progress: trailProgress,
          timeProgress: indexProgress,
          mode: 'progressive',
          markers: markers ?? {
            start: true,
            finish: true,
            current: false,
            head: false
          },
          autoFitBounds: fitBoundsNow,
          deltas: playerLocationDeltas,
          segments: trailSegments,
          coordinates: effectiveCoordinates
        });
        if (fitBoundsNow) hasFitBoundsRef.current = true;
      } else if (rendererRef.current) {
        rendererRef.current.clear();
      }
    };
    if (map.isStyleLoaded()) {
      requestAnimationFrame(() => {
        if (!cancelled) renderTrail();
      });
    } else {
      // Style not loaded yet — wait for it, then render
      const onLoad = () => {
        if (!cancelled) renderTrail();
      };
      map.once('load', onLoad);
      return () => {
        cancelled = true;
        map.off('load', onLoad);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [isRendererReady, visible, session, map, playerLocationDeltas, markers, autoFitBounds, state.progress, trailDistances, trailSegments, effectiveTrail, effectiveCoordinates]);

  // Activate/deactivate renderer and listen for map idle as a render safety net.
  // The 'idle' event fires after the map finishes rendering all tiles and sources,
  // catching edge cases where the initial render was missed due to timing.
  useEffect(() => {
    if (!rendererRef.current) return;
    if (visible && session) {
      rendererRef.current.activate();
      const onIdle = () => {
        if (!rendererRef.current || !isMapActive(map)) return;
        const hasCoords_0 = effectiveCoordinates && effectiveCoordinates.length > 1;
        const fitBoundsNow_0 = autoFitBounds && !hasFitBoundsRef.current && hasCoords_0;
        rendererRef.current.render({
          session,
          progress: timeToDistanceProgress(effectiveTrail, state.progress, trailDistances),
          timeProgress: timeToIndexProgress(effectiveTrail, state.progress),
          mode: 'progressive',
          markers: markers ?? {
            start: true,
            finish: true,
            current: false,
            head: false
          },
          autoFitBounds: fitBoundsNow_0,
          deltas: playerLocationDeltas,
          segments: trailSegments,
          coordinates: effectiveCoordinates
        });
        if (fitBoundsNow_0) hasFitBoundsRef.current = true;
      };
      map?.once('idle', onIdle);
      return () => {
        map?.off('idle', onIdle);
        rendererRef.current?.deactivate();
      };
    }
  }, [visible, session, map, effectiveTrail, state.progress, trailDistances, markers, playerLocationDeltas, trailSegments, effectiveCoordinates, autoFitBounds]);
  useEffect(() => {
    if (!isRendererReady || !eventRendererRef.current || !isMapActive(map)) return;
    if (visible && session) {
      eventRendererRef.current.render(session, playerLocationDeltas ?? null);
    } else {
      eventRendererRef.current.clear();
    }
    return () => {
      eventRendererRef.current?.clear();
    };
  }, [isRendererReady, visible, session, map, playerLocationDeltas]);

  // Playback controls
  const play = useCallback(() => {
    if (state.progress >= 1.0) {
      setState(prev_2 => ({
        ...prev_2,
        progress: 0,
        currentTime: 0,
        isPlaying: true
      }));
      accumulatedTimeRef.current = 0;
    } else {
      setState(prev_3 => ({
        ...prev_3,
        isPlaying: true
      }));
    }
  }, [state.progress]);
  const pause = useCallback(() => {
    setState(prev_4 => ({
      ...prev_4,
      isPlaying: false
    }));
  }, []);
  const restart = useCallback(() => {
    hasFitBoundsRef.current = false;
    setState(prev_5 => ({
      ...prev_5,
      progress: 0,
      currentTime: 0,
      isPlaying: false
    }));
    accumulatedTimeRef.current = 0;
  }, []);
  const jumpToEnd = useCallback(() => {
    setState(prev_6 => ({
      ...prev_6,
      progress: 1.0,
      currentTime: totalDuration,
      isPlaying: false
    }));
    accumulatedTimeRef.current = totalDuration;
  }, [totalDuration]);
  const setProgress = useCallback((progress: number) => {
    const clampedProgress = Math.max(0, Math.min(1, progress));
    const newCurrentTime_0 = clampedProgress * totalDuration;
    setState(prev_7 => ({
      ...prev_7,
      progress: clampedProgress,
      currentTime: newCurrentTime_0,
      isPlaying: false
    }));
    accumulatedTimeRef.current = newCurrentTime_0;
  }, [totalDuration]);
  const clear = useCallback(() => {
    rendererRef.current?.clear();
    eventRendererRef.current?.clear();
  }, []);
  return {
    ...state,
    play,
    pause,
    restart,
    jumpToEnd,
    setProgress,
    formattedCurrentTime: formatVideoDurationSeconds(state.currentTime / 1000) ?? '00:00',
    formattedTotalTime: formatVideoDurationSeconds(totalDuration / 1000) ?? '00:00',
    clear
  };
};